import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { AppInfo } from "./AppInfo";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { env } from "shared-ts-utils-env";
import { AppInfoContext } from "./AppInfoContext";

export interface AppInfoProviderProps {
  appInfo: AppInfo;
  getBuildNumber?: () => Promise<string>;
  // Rendered while the build-number query is pending. Receives the query
  // so callers can pass it to BootingPage (or similar) for a consistent
  // booting display. If omitted, nothing is rendered during loading.
  loadingComponent?: (
    buildNumberQuery: UseQueryResult<string | null, Error>,
  ) => ReactNode;
  children: ReactNode | ((appInfo: AppInfo) => ReactNode);
}

export const AppInfoProvider: FC<AppInfoProviderProps> = (props) => {
  const [buildVersion, setBuildVersion] = useState<string | undefined>();
  const buildNumberFromServer: UseQueryResult<string | null, Error> = useQuery({
    queryKey: ["AppInfoProvider.getBuildNumber"],
    queryFn: () => props.getBuildNumber?.() ?? null,
    enabled: true,
    staleTime: 10 * 60 * 60 * 60 * 1000,
    gcTime: 15 * 60 * 60 * 60 * 1000,
  });

  const appInfo: AppInfo = useMemo(() => {
    if (buildNumberFromServer.data !== undefined) {
      console.info(
        "AppInfoProvider read BUILD_NUMBER from server: " +
          buildNumberFromServer.data,
      );
    }
    const resolvedBuildVersion =
      buildNumberFromServer.data != null
        ? buildNumberFromServer.data
        : buildVersion;
    return {
      ...props.appInfo,
      buildVersion: resolvedBuildVersion,
    };
  }, [props.appInfo, buildNumberFromServer.data, buildVersion]);

  useEffect(() => {
    const buildNumber = env("BUILD_NUMBER");
    console.info(
      "AppInfoProvider reading BUILD_NUMBER from env: " + buildNumber,
    );
    setBuildVersion(buildNumber);
  }, []);

  return (
    <AppInfoContext.Provider value={appInfo}>
      {buildNumberFromServer.isPending && props.loadingComponent
        ? props.loadingComponent(buildNumberFromServer)
        : typeof props.children === "function"
          ? props.children(appInfo)
          : props.children}
    </AppInfoContext.Provider>
  );
};
